import { api } from '../slices/api'; // Adjust the import path as necessary
import {
	AqlTable,
	Gage,
	GageCategory,
	GageType,
	GageCalibration,
	GageCalibrationDocument,
	PaginatedGageResponse,
	PaginatedInspectionPlanResponse,
	InspectionSample,
	KeyenceReport,
	KeyenceSampleReport,
	InspectionPlan,
	KeyenceProgram,
	KeyenceSystem,
	KeyenceValidationComparison,
	KeyenceValidationReport,
	KeyenceValidationReportQuery,
	KeyenceValidationStats,
	PaginatedKeyenceValidationReports
} from '../../interfaces/Quality.Interface'; // Adjust the import path and interfaces as necessary

const validationReportParams = (params: KeyenceValidationReportQuery) => {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			search.append(key, String(value));
		}
	});
	return search.toString();
};

/** Encode get_related_files query params. URLSearchParams percent-encodes
 *  non-ASCII as UTF-8 (so É → %C3%89) and also stops `& # + =` in a part
 *  name from injecting extra query parameters — the actual bug this
 *  change fixes. Browsers already UTF-8-encoded raw interpolation. */
export const relatedFilesQueryString = ({
	jobProduct,
	view,
	device_name,
	include_existing,
}: {
	jobProduct: string;
	view: string;
	device_name?: string;
	/** When true, return every device CSV — including ones already linked. */
	include_existing?: boolean;
}): string => {
	const params = new URLSearchParams();
	params.set('job_product', jobProduct);
	params.set('view', view);
	if (device_name) {
		params.set('device_name', device_name);
	}
	if (include_existing) {
		params.set('include_existing', 'true');
	}
	return params.toString();
};

export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getGages: builder.query<PaginatedGageResponse, { [key: string]: any }>({
			query: (params) => {
				const queryString = new URLSearchParams(params).toString();
				return `quality/gages/?${queryString}`;
			},
		}),
		getGage: builder.query<Gage, string>({
			query: (id) => `quality/gages/${id}/`,
		}),
		createGage: builder.mutation<Gage, Partial<Gage> | FormData>({
			query: (newGage) => ({
				url: 'quality/gages/',
				method: 'POST',
				body: newGage,
			}),
		}),
		updateGage: builder.mutation<Gage, { id: string; values: Partial<Gage> }>({
			query: ({ id, values }) => ({
				url: `quality/gages/${id}/`,
				method: 'PATCH',
				body: values,
			}),
		}),
		deleteGage: builder.mutation<{ success: boolean; id: string }, string>({
			query: (id) => ({
				url: `quality/gages/${id}/`,
				method: 'DELETE',
			}),
		}),
		exportGages: builder.mutation<
			{ status: string; url: string; warning?: string },
			void
		>({
			query: () => ({
				url: 'quality/gages/export/',
				method: 'POST',
			}),
		}),
		getGageCategories: builder.query<GageCategory[], void>({
			query: () => 'quality/gage-categories/',
		}),
		getGageCategory: builder.query<GageCategory, string>({
			query: (id) => `quality/gage-categories/${id}/`,
		}),
		createGageCategory: builder.mutation<GageCategory, Partial<GageCategory>>({
			query: (newCategory) => ({
				url: 'quality/gage-categories/',
				method: 'POST',
				body: newCategory,
			}),
		}),
		updateGageCategory: builder.mutation<
			GageCategory,
			{ id: string; updatedCategory: Partial<GageCategory> }
		>({
			query: ({ id, updatedCategory }) => ({
				url: `quality/gage-categories/${id}/`,
				method: 'PATCH',
				body: updatedCategory,
			}),
		}),
		deleteGageCategory: builder.mutation<
			{ success: boolean; id: string },
			string
		>({
			query: (id) => ({
				url: `quality/gage-categories/${id}/`,
				method: 'DELETE',
			}),
		}),
		getGageTypes: builder.query<GageType[], { category?: string }>({
			query: ({ category }) => {
				let url = 'quality/gage-types/';
				if (category) {
					url += `?category=${category}`;
				}
				return url;
			},
		}),
		getGageType: builder.query<GageType, string>({
			query: (id) => `quality/gage-types/${id}/`,
		}),
		createGageType: builder.mutation<GageType, Partial<GageType>>({
			query: (newType) => ({
				url: 'quality/gage-types/',
				method: 'POST',
				body: newType,
			}),
		}),
		updateGageType: builder.mutation<
			GageType,
			{ id: string; updatedType: Partial<GageType> }
		>({
			query: ({ id, updatedType }) => ({
				url: `quality/gage-types/${id}/`,
				method: 'PATCH',
				body: updatedType,
			}),
		}),
		deleteGageType: builder.mutation<{ success: boolean; id: string }, string>({
			query: (id) => ({
				url: `quality/gage-types/${id}/`,
				method: 'DELETE',
			}),
		}),
		getGageCalibrations: builder.query<GageCalibration[], void>({
			query: () => 'quality/gage-calibrations/',
		}),
		getGageCalibration: builder.query<GageCalibration, string>({
			query: (id) => `quality/gage-calibrations/${id}/`,
		}),
		createGageCalibration: builder.mutation<
			GageCalibration,
			Partial<GageCalibration>
		>({
			query: (newCalibration) => ({
				url: 'quality/gage-calibrations/',
				method: 'POST',
				body: newCalibration,
			}),
		}),
		updateGageCalibration: builder.mutation<
			GageCalibration,
			{ id: string; updatedCalibration: Partial<GageCalibration> }
		>({
			query: ({ id, updatedCalibration }) => ({
				url: `quality/gage-calibrations/${id}/`,
				method: 'PATCH',
				body: updatedCalibration,
			}),
		}),
		deleteGageCalibration: builder.mutation<
			{ success: boolean; id: string },
			string
		>({
			query: (id) => ({
				url: `quality/gage-calibrations/${id}/`,
				method: 'DELETE',
			}),
		}),
		getGageCalibrationDocuments: builder.query<GageCalibrationDocument[], void>(
			{
				query: () => 'quality/gage-calibration-documents/',
			}
		),
		getGageCalibrationDocument: builder.query<GageCalibrationDocument, string>({
			query: (id) => `quality/gage-calibration-documents/${id}/`,
		}),
		createGageCalibrationDocument: builder.mutation<
			GageCalibrationDocument,
			Partial<GageCalibrationDocument>
		>({
			query: (newDocument) => ({
				url: 'quality/gage-calibration-documents/',
				method: 'POST',
				body: newDocument,
			}),
		}),
		updateGageCalibrationDocument: builder.mutation<
			GageCalibrationDocument,
			{ id: string; updatedDocument: Partial<GageCalibrationDocument> }
		>({
			query: ({ id, updatedDocument }) => ({
				url: `quality/gage-calibration-documents/${id}/`,
				method: 'PATCH',
				body: updatedDocument,
			}),
		}),
		deleteGageCalibrationDocument: builder.mutation<
			{ success: boolean; id: string },
			string
		>({
			query: (id) => ({
				url: `quality/gage-calibration-documents/${id}/`,
				method: 'DELETE',
			}),
		}),
		getInspectionSamples: builder.query<
			InspectionSample[],
			{ [key: string]: any }
		>({
			query: (params) => {
				const queryString = new URLSearchParams(params).toString();
				return `quality/inspection-samples/?${queryString}`;
			},
		}),
		getInspectionSample: builder.query<InspectionSample, string>({
			query: (id) => `quality/inspection-samples/${id}/`,
		}),
		getInspectionSamplesByInspectionPlan: builder.query<
			InspectionSample[],
			{ planId?: string }
		>({
			query: ({ planId }) => ({
				url: 'quality/inspection-samples/by-inspection-plan/',
				params: { plan_id: planId },
			}),
			providesTags: (result) =>
				result && Array.isArray(result)
					? [
						...result.map(({ id }) => ({ type: 'InspectionSample' as const, id })),
						{ type: 'InspectionSample' as const, id: 'LIST' },
					]
					: [{ type: 'InspectionSample' as const, id: 'LIST' }],
		}),
		createInspectionSample: builder.mutation<
			InspectionSample,
			Partial<InspectionSample> | FormData
		>({
			query: (newSample) => ({
				url: 'quality/inspection-samples/',
				method: 'POST',
				body: newSample,
			}),
		}),
		updateInspectionSample: builder.mutation<
			InspectionSample,
			{ id: string; values: Partial<InspectionSample> }
		>({
			query: ({ id, values }) => ({
				url: `quality/inspection-samples/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'InspectionSample' as const, id },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		deleteInspectionSample: builder.mutation<
			{ success: boolean; id: string },
			string
		>({
			query: (id) => ({
				url: `quality/inspection-samples/${id}/`,
				method: 'DELETE',
			}),
		}),
		getInspectionResultsByBalloons: builder.query({
			query: (balloonIds: string[]) => ({
				url: 'quality/inspection-results/by-balloons/',
				method: 'POST',
				body: { balloon_ids: balloonIds },
			}),
			providesTags: (result) =>
				result && Array.isArray(result)
					? [
						...result.map(({ id }: { id: string }) => ({ type: 'InspectionResults' as const, id })),
						{ type: 'InspectionResults' as const, id: 'LIST' },
					]
					: [{ type: 'InspectionResults' as const, id: 'LIST' }],
		}),
		updateInspectionResult: builder.mutation({
			query: (updatedResult) => ({
				url: `quality/inspection-results/${updatedResult.id}/`,
				method: 'PATCH',
				body: updatedResult,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'InspectionResults' as const, id },
				{ type: 'InspectionResults' as const, id: 'LIST' },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionPlan' as const, id: 'LIST' },
			],
		}),

		getKeyenceReports: builder.query<KeyenceReport[], { [key: string]: any }>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `quality/keyence-reports-list/?${queryParams.toString()}`;
			},
		}),
		getInspectionPlans: builder.query<PaginatedInspectionPlanResponse, { [key: string]: any }>(
			{
				query: (params) => {
					const queryParams = new URLSearchParams();
					Object.entries(params).forEach(([key, value]) => {
						if (value !== undefined && value !== null && value !== '') {
							queryParams.append(key, value.toString());
						}
					});
					return `quality/inspection-plans/?${queryParams.toString()}`;
				},
			}
		),
		getInspectionPlanByProject: builder.query<
			InspectionPlan,
			{
				projectId: string;
				samples?: 'ids' | 'full';
				project_mode?: 'id' | 'full' | 'condensed';
				device_name?: string;
			}
		>({
			query: ({ projectId, samples = 'full', project_mode = 'full', device_name }) => {
				const params: Record<string, string> = {
					samples,
					project_mode,
				};
				if (device_name) {
					params.device_name = device_name;
				}
				const queryString = new URLSearchParams(params).toString();
				return {
					url: `quality/inspection-plans/by-project/${projectId}?${queryString}`,
					method: 'GET',
				};
			},
			providesTags: (result, error, { projectId }) => [
				{ type: 'InspectionPlan' as const, id: projectId },
				{ type: 'InspectionPlan' as const, id: result?.id ?? 'LIST' },
			],
		}),
		getInspectionSamplesByPlan: builder.query<
			InspectionSample[],
			{ planId?: string }
		>({
			query: ({ planId }) => ({
				url: `quality/inspection-samples/by-plan/${planId}`,
			}),
			providesTags: (result, error, { planId }) => [
				{ type: 'InspectionSample' as const, id: planId ?? 'LIST' },
			],
		}),
		getInspectionSamplesByProject: builder.query<
			InspectionSample,
			{ projectId?: string }
		>({
			query: (projectId) => ({
				url: `quality/inspection-samples/by-project/${projectId}`,
			}),
			providesTags: (result, error, { projectId }) => [
				{ type: 'InspectionSample' as const, id: projectId ?? 'LIST' },
			],
		}),
		resetInspectionSample: builder.mutation({
			query: ({ id }) => ({
				url: `quality/inspection-samples/${id}/reset/`,
				method: 'POST'
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'InspectionSample' as const, id },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		getInspectionPlanByLineItem: builder.query<
			InspectionPlan,
			{ lineItemId?: string }
		>({
			query: ({ lineItemId }) => ({
				url: 'quality/inspection-plans/by-project/',
				params: { line_item_id: lineItemId },
			}),
		}),
		linkKeyenceReport: builder.mutation({
			query: (body) => ({
				url: 'quality/keyence-reports/link/',
				method: 'POST',
				body,
			}),
			invalidatesTags: () => [
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionResults' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		overrideKeyenceReport: builder.mutation({
			query: (body) => ({
				url: 'quality/keyence-reports/override/',
				method: 'POST',
				body,
			}),
			invalidatesTags: () => [
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionResults' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		unlinkKeyenceReport: builder.mutation({
			query: (body) => ({
				url: 'quality/keyence-reports/unlink/',
				method: 'POST',
				body,
			}),
			invalidatesTags: () => [
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionResults' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		deleteKeyenceReport: builder.mutation({
			query: (body ) => ({
				url: `quality/keyence-reports/delete/`,
				method: 'POST',
				body
			}),
			invalidatesTags: () => [
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionResults' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		overwriteKeyenceReport: builder.mutation({
			query: (body) => ({
				url: 'quality/keyence-reports/overwrite/',
				method: 'POST',
				body,
			}),
			invalidatesTags: () => [
				{ type: 'InspectionPlan' as const, id: 'LIST' },
				{ type: 'InspectionSample' as const, id: 'LIST' },
				{ type: 'InspectionResults' as const, id: 'LIST' },
				'OrderLineItem',
			],
		}),
		getRelatedFiles: builder.query<
			KeyenceReport[],
			{ jobProduct: string; view: string; device_name?: string; include_existing?: boolean }
		>({
			query: ({ jobProduct, view, device_name, include_existing }) => ({
				url: `quality/keyence-reports/get_related_files/?${relatedFilesQueryString({
					jobProduct,
					view,
					device_name,
					include_existing,
				})}`,
				method: 'GET',
			}),
		}),
		getKeyenceSampleReport: builder.query<
			KeyenceSampleReport,
			{ jobProduct: string; view: string; fileName: string; device_name?: string }
		>({
			query: ({ jobProduct, view, fileName, device_name }) => {
				const params = new URLSearchParams(
					relatedFilesQueryString({ jobProduct, view, device_name })
				);
				params.set('file_name', fileName);
				return {
					url: `quality/keyence-reports/sample_report/?${params.toString()}`,
					method: 'GET',
				};
			},
		}),
		getStoredKeyenceSampleReport: builder.query<KeyenceSampleReport, string>({
			query: (reportId) => ({
				url: `quality/keyence-reports/${reportId}/results/`,
				method: 'GET',
			}),
		}),
		uploadKeyencePrograms: builder.mutation<KeyenceProgram[], FormData>({
			query: (formData) => ({
				url: 'quality/keyence-programs/upload/',
				method: 'POST',
				body: formData,
				timeout: 600000,
			}),
			invalidatesTags: [
				{ type: 'KeyenceProgram', id: 'LIST' },
				{ type: 'KeyenceValidationReport', id: 'LIST' },
				{ type: 'KeyenceValidationReport', id: 'STATS' },
			],
		}),
		syncKeyenceProgram: builder.mutation<any, string>({
			query: (id) => ({
				url: `quality/keyence-programs/${id}/sync/`,
				method: 'POST',
				timeout: 600000,
			}),
		}),
		// Opposite direction to syncKeyenceProgram: sends the ERP's stored copy
		// OUT to the device, for programs uploaded while the proxy was down.
		pushKeyenceProgramToDevice: builder.mutation<any, string>({
			query: (id) => ({
				url: `quality/keyence-programs/${id}/push-to-device/`,
				method: 'POST',
				timeout: 600000,
			}),
		}),
		syncFromKeyence: builder.mutation<any, { projectId: string }>({
			query: ({ projectId }) => ({
				url: 'quality/keyence-programs/sync-from-keyence/',
				method: 'POST',
				body: { project_id: projectId },
				timeout: 600000,
			}),
		}),
		getRecentFile: builder.query<InspectionPlan, { device_name?: string; production_method?: string; building_id?: string } | void>({
			query: (args) => {
				let url = `quality/inspection-plans/get_recent_file`;
				const params = new URLSearchParams();
				if (args && args.device_name) {
					params.append('device_name', args.device_name);
				}
				if (args && args.production_method) {
					params.append('production_method', args.production_method);
				}
				if (args && args.building_id) {
					params.append('building_id', args.building_id);
				}
				if (params.toString()) {
					url += `?${params.toString()}`;
				}
				return {
					url,
					method: 'GET',
				};
			},
		}),
		getKeyenceSystems: builder.query<KeyenceSystem[], { building_id?: string; include_unassigned?: boolean } | void>({
			query: (args) => {
				const params = new URLSearchParams();
				if (args && args.building_id) {
					params.append('building_id', args.building_id);
				}
				if (args && args.include_unassigned) {
					params.append('include_unassigned', 'true');
				}
				const qs = params.toString();
				return qs ? `quality/keyence-systems/?${qs}` : 'quality/keyence-systems/';
			},
		}),
		getKeyenceProgramsByProject: builder.query<
			KeyenceProgram[],
			{ projectId?: string }
		>({
			query: ({ projectId }) => {
				return `quality/keyence-programs/by-project/${projectId}`;
			},
			providesTags: [{ type: 'KeyenceProgram', id: 'LIST' }],
		}),

		getKeyenceValidationReports: builder.query<
			PaginatedKeyenceValidationReports,
			KeyenceValidationReportQuery
		>({
			query: (params) => {
				const qs = validationReportParams(params);
				return qs
					? `quality/keyence-validation-reports/?${qs}`
					: 'quality/keyence-validation-reports/';
			},
			providesTags: [{ type: 'KeyenceValidationReport', id: 'LIST' }],
		}),
		getKeyenceValidationReport: builder.query<KeyenceValidationReport, string>({
			query: (id) => `quality/keyence-validation-reports/${id}/`,
			providesTags: (_result, _error, id) => [
				{ type: 'KeyenceValidationReport', id },
			],
		}),
		getKeyenceValidationStats: builder.query<
			KeyenceValidationStats,
			KeyenceValidationReportQuery
		>({
			query: (params) => {
				const qs = validationReportParams(params);
				return qs
					? `quality/keyence-validation-reports/stats/?${qs}`
					: 'quality/keyence-validation-reports/stats/';
			},
			providesTags: [{ type: 'KeyenceValidationReport', id: 'STATS' }],
		}),
		getKeyenceValidationComparison: builder.query<
			KeyenceValidationComparison,
			string
		>({
			query: (id) => `quality/keyence-validation-reports/${id}/comparison/`,
			providesTags: (_result, _error, id) => [
				{ type: 'KeyenceValidationReport', id: `COMPARISON-${id}` },
			],
		}),
		markKeyenceValidationFindingFalsePositive: builder.mutation<
			KeyenceValidationReport,
			{ reportId: string; findingId: string }
		>({
			query: ({ reportId, findingId }) => ({
				url: `quality/keyence-validation-reports/${reportId}/findings/${findingId}/mark-false-positive/`,
				method: 'POST',
			}),
			invalidatesTags: (_result, _error, { reportId }) => [
				{ type: 'KeyenceValidationReport', id: reportId },
				{ type: 'KeyenceValidationReport', id: 'LIST' },
				{ type: 'KeyenceValidationReport', id: 'STATS' },
				{ type: 'KeyenceValidationReport', id: `COMPARISON-${reportId}` },
			],
		}),
		unmarkKeyenceValidationFindingFalsePositive: builder.mutation<
			KeyenceValidationReport,
			{ reportId: string; findingId: string }
		>({
			query: ({ reportId, findingId }) => ({
				url: `quality/keyence-validation-reports/${reportId}/findings/${findingId}/unmark-false-positive/`,
				method: 'POST',
			}),
			invalidatesTags: (_result, _error, { reportId }) => [
				{ type: 'KeyenceValidationReport', id: reportId },
				{ type: 'KeyenceValidationReport', id: 'LIST' },
				{ type: 'KeyenceValidationReport', id: 'STATS' },
				{ type: 'KeyenceValidationReport', id: `COMPARISON-${reportId}` },
			],
		}),

		getAqlTables: builder.query<AqlTable[], void>({
			query: () => 'quality/aql-tables/',
		}),
	}),
	overrideExisting: false,
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetGagesQuery,
	useGetGageQuery,
	useCreateGageMutation,
	useUpdateGageMutation,
	useDeleteGageMutation,
	useExportGagesMutation,
	useGetGageCategoriesQuery,
	useGetGageCategoryQuery,
	useCreateGageCategoryMutation,
	useUpdateGageCategoryMutation,
	useDeleteGageCategoryMutation,
	useGetGageTypesQuery,
	useGetGageTypeQuery,
	useCreateGageTypeMutation,
	useUpdateGageTypeMutation,
	useDeleteGageTypeMutation,
	useGetGageCalibrationsQuery,
	useGetGageCalibrationQuery,
	useCreateGageCalibrationMutation,
	useUpdateGageCalibrationMutation,
	useDeleteGageCalibrationMutation,
	useGetGageCalibrationDocumentsQuery,
	useGetGageCalibrationDocumentQuery,
	useCreateGageCalibrationDocumentMutation,
	useUpdateGageCalibrationDocumentMutation,
	useDeleteGageCalibrationDocumentMutation,
	useGetInspectionSampleQuery,
	useLazyGetInspectionSampleQuery,
	useGetInspectionSamplesByInspectionPlanQuery,
	useGetInspectionSamplesQuery,
	useCreateInspectionSampleMutation,
	useUpdateInspectionSampleMutation,
	useDeleteInspectionSampleMutation,
	useGetInspectionResultsByBalloonsQuery,
	useUpdateInspectionResultMutation,
	useGetKeyenceReportsQuery,
	useGetInspectionPlansQuery,
	useGetInspectionPlanByLineItemQuery,
	useGetInspectionPlanByProjectQuery,
	useGetInspectionSamplesByProjectQuery,
	useGetInspectionSamplesByPlanQuery,
	useLinkKeyenceReportMutation,
	useUnlinkKeyenceReportMutation,
	useOverrideKeyenceReportMutation,
	useOverwriteKeyenceReportMutation,
	useDeleteKeyenceReportMutation,
	useGetRelatedFilesQuery,
	useGetKeyenceSampleReportQuery,
	useGetStoredKeyenceSampleReportQuery,
	useUploadKeyenceProgramsMutation,
	useGetRecentFileQuery,
	useLazyGetRecentFileQuery,
	useGetKeyenceProgramsByProjectQuery,
	useResetInspectionSampleMutation,
	useGetKeyenceSystemsQuery,
	useSyncKeyenceProgramMutation,
	usePushKeyenceProgramToDeviceMutation,
	useSyncFromKeyenceMutation,
	useGetKeyenceValidationReportsQuery,
	useGetKeyenceValidationReportQuery,
	useGetKeyenceValidationStatsQuery,
	useGetKeyenceValidationComparisonQuery,
	useMarkKeyenceValidationFindingFalsePositiveMutation,
	useUnmarkKeyenceValidationFindingFalsePositiveMutation,
	useGetAqlTablesQuery,
} = extendedApiSlice;
